import { ErrorBoundary, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { RouteProp, useRoute } from '@react-navigation/native';
import { Box, ScrollView, Skeleton, View } from 'native-base';
import React from 'react';

import { SignedInRootStackParamList } from '../../../../routes/types';
import { messageError } from '../../../Resume/utils';

import { Customer } from './components/Customer';
import { EditProduct } from './components/EditProduct';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { SectionWrapper } from './components/SectionWrapper';
import { useCustomer } from './hooks/customer';
import { useOrder } from './hooks/order';
import { useProduct } from './hooks/product';

interface EquityOrderEditProps {}

export const ContainerWrapper = ({ children }) => {
  const isDesktop = useBreakpointValue({ base: false, lg: true });

  if (isDesktop) {
    return (
      <VStack pb={Spacing['inline.nano']} space={Spacing['inline.xxs']}>
        {children}
      </VStack>
    );
  }

  return (
    <ScrollView
      showsVerticalScrollIndicator={false}
      px={Spacing['inline.xxxs']}
      pb={Spacing['inline.nano']}>
      <VStack space={Spacing['inline.xxs']}>{children}</VStack>
    </ScrollView>
  );
};

export const EquityOrderEdit: React.FC<EquityOrderEditProps> = () => {
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'EquityOrderEdit'>>();

  const customerAccountNumber = params?.data?.account.accountNumber!;
  const equityOperationType = params?.data?.sideDescription || messageError;

  const {
    productData,
    productDataIsError,
    productDataIsLoading,
    amountItem,
    handleChangeAmountItem,
    handleChangePriceItem,
    priceItem,
    totalPrice,
    orderIsValid,
  } = useProduct({
    data: params.data,
  });

  const { customerData, customerDataIsError, customerDataIsLoading } = useCustomer({
    customerAccountNumber,
  });

  const { handleUpdateEquityOrder } = useOrder({
    customer: customerData?.data,
    equityOperationType,
  });

  const isLoading = productDataIsLoading || customerDataIsLoading;
  const isError = customerDataIsError || productDataIsError;

  const Wrapper = useBreakpointValue({
    base: ({ children }) => <View minW="container">{children}</View>,
    lg: ({ children }) => (
      <ScrollView showsVerticalScrollIndicator={false}>
        <View minW="container">{children}</View>
      </ScrollView>
    ),
  });

  const Container = ({ children }: { children: React.ReactNode }) => {
    return (
      <Wrapper>
        <ContainerWrapper>
          <Header />
          {children}
          <Footer
            sendButtonIsDisabled={!orderIsValid}
            onUpdateOrder={() =>
              handleUpdateEquityOrder({
                orderId: params?.data?.orderId!,
                requestedAmount: priceItem,
                requestedQuantity: amountItem,
              })
            }
          />
        </ContainerWrapper>
      </Wrapper>
    );
  };

  if (isLoading) {
    return (
      <Container>
        <Skeleton
          w="full"
          h="180px"
          borderRadius={BoxRadius.sm}
          endColor={Color['brand.primary.lightest']}
        />
        <Skeleton
          w="full"
          h="250px"
          borderRadius={BoxRadius.sm}
          endColor={Color['brand.primary.lightest']}
        />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container>
        <Box py={{ sm: Spacing['inline.md'] }} bg="white">
          <ErrorBoundary
            errorTitle="Ops, Algo deu errado!"
            errorMessage="No momento não conseguimos te mostrar os resultados. Tente novamente em alguns instantes"
          />
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <SectionWrapper title="Cliente selecionado">
        <Customer data={customerData?.data} totalPrice={totalPrice} />
      </SectionWrapper>
      <SectionWrapper title="Editar produto">
        <EditProduct
          data={productData}
          amountItem={amountItem}
          handleChangeAmountItem={handleChangeAmountItem}
          handleChangePriceItem={handleChangePriceItem}
          priceItem={priceItem}
          totalPrice={totalPrice}
          equityOperationType={equityOperationType}
        />
      </SectionWrapper>
    </Container>
  );
};
